import { useEffect, useRef, useState } from "react";
import { Search, X, Crown, ChevronLeft } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { TemplateOut, TemplatesPage } from "../types";

/** How long the user must stop typing before the lookup runs. */
const IDLE_MS = 700;

// Template total is the same for the whole session - fetch it once.
let totalPromise: Promise<number> | null = null;
function loadTotal(): Promise<number> {
  if (!totalPromise) {
    totalPromise = api
      .get<TemplatesPage>("/api/templates?page=0")
      .then((r) => r.total)
      .catch((err) => {
        totalPromise = null;
        throw err;
      });
  }
  return totalPromise;
}

type Result =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "found"; template: TemplateOut }
  | { kind: "error"; message: string };

export function TemplateNumberSearch({ onPickTemplate }: { onPickTemplate: (tpl: TemplateOut) => void }) {
  const [value, setValue] = useState("");
  const [total, setTotal] = useState<number | null>(null);
  const [result, setResult] = useState<Result>({ kind: "idle" });
  const timerRef = useRef<number | null>(null);
  const requestRef = useRef(0); // ignores answers that arrive after a newer lookup started

  useEffect(() => {
    loadTotal()
      .then(setTotal)
      .catch(() => undefined);
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  const maxDigits = total ? String(total).length : 4;

  const lookup = async (raw: string) => {
    const my = ++requestRef.current;
    const n = Number(raw);
    if (!raw || !Number.isInteger(n)) {
      setResult({ kind: "idle" });
      return;
    }
    let max = total;
    if (max === null) {
      try {
        max = await loadTotal();
        setTotal(max);
      } catch {
        if (my === requestRef.current) setResult({ kind: "error", message: "تعذر الاتصال، حاول مرة أخرى" });
        return;
      }
    }
    if (n < 1 || n > max) {
      setResult({ kind: "error", message: `اكتب رقماً من 1 إلى ${max}` });
      return;
    }
    setResult({ kind: "loading" });
    try {
      const tpl = await api.get<TemplateOut>(`/api/templates/template_${n}`);
      if (my === requestRef.current) setResult({ kind: "found", template: tpl });
    } catch (err) {
      if (my !== requestRef.current) return;
      setResult({
        kind: "error",
        message: err instanceof ApiError && err.status === 404 ? "لا يوجد قالب بهذا الرقم" : "تعذر تحميل القالب",
      });
    }
  };

  const schedule = (raw: string) => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void lookup(raw);
    }, IDLE_MS);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // digits only (also converts Arabic-Indic digits typed from an Arabic keyboard)
    const digits = e.target.value
      .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
      .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
      .replace(/\D/g, "")
      .slice(0, maxDigits);
    setValue(digits);
    // While typing nothing is shown: drop any old result and wait for the pause.
    requestRef.current++;
    setResult({ kind: "idle" });
    if (!digits) {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      return;
    }
    schedule(digits);
  };

  const clear = () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    requestRef.current++;
    setValue("");
    setResult({ kind: "idle" });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="glass-card flex items-center gap-2 px-3.5 h-12">
        <Search size={18} strokeWidth={2} className="text-tg-hint shrink-0" />
        <input
          value={value}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              if (timerRef.current !== null) window.clearTimeout(timerRef.current);
              (e.target as HTMLInputElement).blur();
              void lookup(value);
            }
          }}
          inputMode="numeric"
          enterKeyHint="search"
          autoComplete="off"
          placeholder={total ? `ابحث برقم القالب (1 - ${total})` : "ابحث برقم القالب"}
          aria-label="بحث برقم القالب"
          className="flex-1 min-w-0 bg-transparent outline-none text-sm text-tg-text placeholder:text-tg-hint tabular-nums"
        />
        {value && (
          <button
            type="button"
            onClick={clear}
            aria-label="مسح"
            className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center shrink-0"
          >
            <X size={14} strokeWidth={2.25} className="text-tg-hint" />
          </button>
        )}
      </div>

      {result.kind === "loading" && <p className="text-xs text-tg-hint px-1">جاري البحث...</p>}

      {result.kind === "error" && <p className="text-xs text-red-400 px-1">{result.message}</p>}

      {result.kind === "found" && (
        <button
          type="button"
          onClick={() => onPickTemplate(result.template)}
          className="glass-card flex items-center gap-3 p-2.5 text-start active:scale-[0.99] transition-transform"
        >
          <img
            src={result.template.preview_url}
            alt=""
            className="w-16 h-16 rounded-xl object-cover bg-black/20 shrink-0"
          />
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-bold text-tg-text truncate">{result.template.name}</span>
            <span className="block text-xs text-tg-hint mt-0.5">
              {result.template.is_premium ? (
                <span className="inline-flex items-center gap-1">
                  <Crown size={12} strokeWidth={2.25} />
                  قالب VIP
                </span>
              ) : (
                "اضغط لاستخدام القالب"
              )}
            </span>
          </span>
          <ChevronLeft size={18} strokeWidth={2} className="text-tg-hint shrink-0" />
        </button>
      )}
    </div>
  );
}
